"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, Camera, Share2, MessageCircle, Check, Heart } from "lucide-react";
import { cn } from "@/lib/utils";

interface ReviewPromptProps {
  orderId: string;
  orderPublicId: string;
  onReviewSubmitted?: () => void;
}

const WHATSAPP_NUMBER = "919876543210";

export function ReviewPrompt({ orderId, orderPublicId, onReviewSubmitted }: ReviewPromptProps) {
  const [step, setStep] = useState<"rating" | "testimonial" | "share" | "done">("rating");
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [testimonial, setTestimonial] = useState("");
  const [photoConsent, setPhotoConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmitReview() {
    if (rating === 0) return;

    setSubmitting(true);
    try {
      const response = await fetch(`/api/customer/orders/${orderId}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rating,
          testimonial: testimonial.trim() || null,
          photoConsent,
        }),
      });

      if (!response.ok) throw new Error("Failed to submit review");

      setStep("share");
      onReviewSubmitted?.();
    } catch (error) {
      toast.error("Couldn't save your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleShareWhatsApp() {
    const message = encodeURIComponent(
      `I just got my outfit from Zaklyn and it fits perfectly! If you need tailoring, DM them "ZAKLYN" on WhatsApp. Highly recommend!`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
    setStep("done");
  }

  function handleUploadPhoto() {
    const whatsappMessage = encodeURIComponent(
      `Hi! I'd like to share a photo of my order ${orderPublicId} for your testimonials.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`, "_blank");
  }

  if (step === "done") {
    return (
      <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
        <CardContent className="pt-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Heart className="h-8 w-8 text-emerald-600" />
          </motion.div>
          <h3 className="font-display text-xl font-semibold text-foreground mb-2">
            Thank You!
          </h3>
          <p className="text-muted-foreground">
            Your feedback helps us serve you better. See you next time!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-amber-50/30 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Star className="h-5 w-5 text-amber-500" />
          How was your experience?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <AnimatePresence mode="wait">
          {step === "rating" && (
            <motion.div
              key="rating"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Tap the stars to rate your order
                </p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={cn(
                          "h-10 w-10 transition-colors",
                          (hoveredRating || rating) >= star
                            ? "fill-amber-400 text-amber-400"
                            : "text-gray-300"
                        )}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-sm text-muted-foreground mt-3"
                  >
                    {rating === 5 && "Amazing! We're so glad you loved it!"}
                    {rating === 4 && "Great! Thanks for the positive feedback!"}
                    {rating === 3 && "Thanks for the honest feedback!"}
                    {rating <= 2 && "We're sorry. Please share how we can improve."}
                  </motion.p>
                )}
              </div>

              {rating > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                >
                  <Button
                    className="w-full"
                    onClick={() => setStep("testimonial")}
                  >
                    Continue
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}

          {step === "testimonial" && (
            <motion.div
              key="testimonial"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Would you like to share a quick testimonial? (Optional)
                </p>
                <Textarea
                  value={testimonial}
                  onChange={(e) => setTestimonial(e.target.value)}
                  placeholder="What did you love about your experience?"
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer",
                  photoConsent
                    ? "bg-primary/5 border-primary/30"
                    : "bg-muted/30 border-muted"
                )}
                onClick={() => setPhotoConsent(!photoConsent)}
              >
                <div
                  className={cn(
                    "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                    photoConsent
                      ? "bg-primary border-primary"
                      : "border-gray-300"
                  )}
                >
                  {photoConsent && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    I&apos;d like to share a photo
                  </p>
                  <p className="text-xs text-muted-foreground">
                    We&apos;ll reach out for a mirror selfie or short video (UGC)
                  </p>
                </div>
                <Camera className="h-5 w-5 text-muted-foreground" />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep("rating")}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting ? "Saving..." : "Submit"}
                </Button>
              </div>
            </motion.div>
          )}

          {step === "share" && (
            <motion.div
              key="share"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">
                  Thanks for your feedback!
                </h3>
                <p className="text-sm text-muted-foreground">
                  Help your friends discover Zaklyn
                </p>
              </div>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3"
                  onClick={handleShareWhatsApp}
                >
                  <Share2 className="h-4 w-4" />
                  Share with friends on WhatsApp
                </Button>

                {photoConsent && (
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3"
                    onClick={handleUploadPhoto}
                  >
                    <Camera className="h-4 w-4" />
                    Send your photo via WhatsApp
                  </Button>
                )}

                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => setStep("done")}
                >
                  Maybe later
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Tell them to DM &quot;ZAKLYN&quot; to get started
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
